import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const DEFAULT_TAIL = 5000;
const MIN_TAIL = 1;
const MAX_TAIL = 50000;
const LEVEL_OPTIONS = ["ALL", "DEBUG", "INFO", "WARNING", "ERROR"];

const formatUtcDatetimeInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  );
};

const buildDefaultDateRange = () => {
  const now = new Date();
  const hourAgo = new Date(now.getTime() - 60 * 60 * 1000);
  return {
    from: formatUtcDatetimeInputValue(hourAgo),
    to: formatUtcDatetimeInputValue(now),
  };
};

function ProductionLogsPage({ lineNo, productionNo }) {
  usePageTitle(
    productionNo
      ? `Logs – Line ${lineNo} / ${productionNo}`
      : "Production Logs"
  );
  const [lines, setLines] = useState([]);
  const [totalLines, setTotalLines] = useState(0);
  const [totalLinesUnfiltered, setTotalLinesUnfiltered] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const [tail, setTail] = useState(DEFAULT_TAIL);
  const [tailInput, setTailInput] = useState(String(DEFAULT_TAIL));
  const [dateFromInput, setDateFromInput] = useState(() => buildDefaultDateRange().from);
  const [dateToInput, setDateToInput] = useState(() => buildDefaultDateRange().to);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [levelInput, setLevelInput] = useState("ALL");
  const [level, setLevel] = useState("ALL");
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const isMountedRef = useRef(true);

  const fetchIntervalMs = useMemo(() => {
    const envInterval = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(envInterval);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!productionNo || lineNo === undefined || lineNo === null || lineNo === "") {
      return;
    }
    try {
      const params = new URLSearchParams({ tail: String(tail) });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (level && level !== "ALL") params.set("level", level);
      const response = await auth.fetchWithAuth(
        `/logs/${encodeURIComponent(lineNo)}/${encodeURIComponent(productionNo)}/?${params.toString()}`
      );
      if (response.status === 404) {
        if (isMountedRef.current) {
          setLines([]);
          setTotalLines(0);
          setTotalLinesUnfiltered(0);
          setTruncated(false);
          setError("No logs available for this order.");
        }
        return;
      }
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setLines(Array.isArray(payload?.lines) ? payload.lines : []);
        setTotalLines(Number(payload?.total_lines) || 0);
        setTotalLinesUnfiltered(Number(payload?.total_lines_unfiltered) || 0);
        setTruncated(Boolean(payload?.truncated));
        setError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [auth.fetchWithAuth, lineNo, productionNo, tail, dateFrom, dateTo, level]);

  const handleTailSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const parsed = Number.parseInt(tailInput, 10);
      if (!Number.isFinite(parsed)) {
        return;
      }
      const clamped = Math.min(MAX_TAIL, Math.max(MIN_TAIL, parsed));
      setTail(clamped);
      setTailInput(String(clamped));
      setDateFrom(dateFromInput);
      setDateTo(dateToInput);
      setLevel(levelInput);
    },
    [tailInput, dateFromInput, dateToInput, levelInput]
  );

  const handleClearDates = useCallback(() => {
    setDateFromInput("");
    setDateToInput("");
    setDateFrom("");
    setDateTo("");
  }, []);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    if (!productionNo || lineNo === undefined || lineNo === null || lineNo === "") {
      setLoading(false);
      setError("Missing line_no or production_no.");
      return;
    }
    setLoading(true);
    let intervalId = null;
    fetchLogs();
    if (autoRefresh) {
      intervalId = setInterval(fetchLogs, fetchIntervalMs);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [auth.authChecked, auth.isAuthenticated, autoRefresh, fetchIntervalMs, fetchLogs, lineNo, productionNo]);

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        <div className="order-container">
          <a
            className="order-back-link"
            href={`/order_logs/${encodeURIComponent(lineNo)}`}
          >
            Back to worker_line_{lineNo}
          </a>
          <h1 className="order-title">
            worker_line_{lineNo} / {productionNo}
          </h1>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={fetchLogs}
            subtitleWhenOn={`Logi odświeżają się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          <form
            className="order-filter"
            onSubmit={handleTailSubmit}
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}
          >
            <label htmlFor="tail-input" style={{ color: "#cbd5f5", fontSize: "0.9rem" }}>
              Ile ostatnich linii pokazać ({MIN_TAIL}–{MAX_TAIL}):
            </label>
            <input
              id="tail-input"
              type="number"
              min={MIN_TAIL}
              max={MAX_TAIL}
              step={1}
              value={tailInput}
              onChange={(e) => setTailInput(e.target.value)}
              style={{ minWidth: "120px" }}
            />
            <label htmlFor="date-from-input" style={{ color: "#cbd5f5", fontSize: "0.9rem" }}>
              Od (UTC):
            </label>
            <input
              id="date-from-input"
              type="datetime-local"
              step={1}
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
            />
            <label htmlFor="date-to-input" style={{ color: "#cbd5f5", fontSize: "0.9rem" }}>
              Do (UTC):
            </label>
            <input
              id="date-to-input"
              type="datetime-local"
              step={1}
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
            />
            <label htmlFor="level-input" style={{ color: "#cbd5f5", fontSize: "0.9rem" }}>
              Poziom:
            </label>
            <select
              id="level-input"
              value={levelInput}
              onChange={(e) => setLevelInput(e.target.value)}
              style={{ minWidth: "160px", padding: "6px 10px", fontSize: "1rem" }}
            >
              {LEVEL_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "ALL" ? "Wszystkie" : opt}
                </option>
              ))}
            </select>
            <button type="submit" className="order-metrics-button">
              Pokaż
            </button>
            <button
              type="button"
              className="order-metrics-button"
              onClick={handleClearDates}
              disabled={!dateFromInput && !dateToInput && !dateFrom && !dateTo}
            >
              Wyczyść daty
            </button>
            <span style={{ color: "#9aa5b1", fontSize: "0.85rem" }}>
              aktualnie: {tail.toLocaleString()}
              {(dateFrom || dateTo) && (
                <> · zakres: {dateFrom || "–∞"} → {dateTo || "+∞"}</>
              )}
              {level && level !== "ALL" && <> · poziom: {level}</>}
            </span>
          </form>
          {truncated && (
            <div className="order-error" style={{ background: "rgba(255,170,0,0.18)" }}>
              Showing last {lines.length} of {totalLines} lines
              {dateFrom || dateTo
                ? ` (filtered from ${totalLinesUnfiltered} total in file).`
                : "."}
            </div>
          )}
          {!truncated && (dateFrom || dateTo) && totalLinesUnfiltered > 0 && (
            <div className="order-error" style={{ background: "rgba(80,160,255,0.16)" }}>
              {totalLines} line{totalLines === 1 ? "" : "s"} match the date filter (of {totalLinesUnfiltered} in file).
            </div>
          )}
          {error && <div className="order-error">Error: {error}</div>}
          {loading ? (
            <div className="order-loader">
              <div className="order-spinner" />
              <p>Loading logs...</p>
            </div>
          ) : lines.length === 0 ? (
            <div>No log lines.</div>
          ) : (
            <pre
              style={{
                background: "#1a1d24",
                color: "#e6e6e6",
                padding: "16px",
                borderRadius: "10px",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "12px",
                lineHeight: "1.45",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: "75vh",
                overflowY: "auto",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {lines.join("\n")}
            </pre>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default ProductionLogsPage;
